import { useState } from "react";
import { Archive } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import { useGoogleDocs } from "@/hooks/useGoogleDocs";
import { ContentActionBar } from "@/components/content/ContentActionBar";

export const ContentDisplay = ({ data, onSave, onEdit, isSaved, onRepurpose, exportMeta, resourcesUsed }) => {
  const { api } = useAuth();
  const { exportToGoogleDocs, gdocsExporting } = useGoogleDocs();
  const [copiedDraft, setCopiedDraft] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    if (type === "draft") {
      setCopiedDraft(true);
      setTimeout(() => setCopiedDraft(false), 2000);
      toast.success("Draft copied!");
    } else {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
      toast.success("All content copied!");
    }
  };

  const getAllText = () => {
    let text = `## Core Angle\n${data.core_angle}\n\n## Draft\n${data.draft}\n\n## Why It Works\n${data.why_it_works}\n\n## Alternate Hook Options\n`;
    data.alternate_hooks?.forEach((h, i) => { text += `${i + 1}. ${h}\n`; });
    if (data.cta_variations?.length) {
      text += `\n## CTA Variations\n`;
      data.cta_variations.forEach((c, i) => { text += `${i + 1}. ${c}\n`; });
    }
    return text;
  };

  const getMarkdownText = () => {
    let md = `## Core Angle\n\n${data.core_angle}\n\n---\n\n## Draft\n\n${data.draft}\n\n---\n\n## Why It Works\n\n${data.why_it_works}\n\n---\n\n## Alternate Hook Options\n\n`;
    data.alternate_hooks?.forEach((h, i) => { md += `${i + 1}. ${h}\n`; });
    if (data.cta_variations?.length) {
      md += `\n---\n\n## CTA Variations\n\n`;
      data.cta_variations.forEach((c, i) => { md += `${i + 1}. ${c}\n`; });
    }
    return md;
  };

  const copyMarkdown = async () => {
    const md = getMarkdownText();
    try {
      await navigator.clipboard.writeText(md);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = md;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedMarkdown(true);
    setTimeout(() => setCopiedMarkdown(false), 2000);
    toast.success("Markdown copied!");
  };

  const openInGoogleDocs = async () => {
    const secs = {
      "Core Angle": data.core_angle || "",
      "Draft": data.draft || "",
      "Why It Works": data.why_it_works || "",
      "Alternate Hook Options": (data.alternate_hooks || []).map((h, i) => `${i + 1}. ${h}`).join("\n"),
    };
    if (data.cta_variations?.length) {
      secs["CTA Variations"] = data.cta_variations.map((c, i) => `${i + 1}. ${c}`).join("\n");
    }
    await exportToGoogleDocs({
      title: exportMeta?.title || exportMeta?.topic || "Quibn Export",
      sections: secs,
    });
  };

  const getHtmlText = () => {
    const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    let html = `<h2>Core Angle</h2>\n<p>${esc(data.core_angle)}</p>\n\n<h2>Draft</h2>\n<p>${esc(data.draft).replace(/\n/g, "<br>")}</p>\n\n<h2>Why It Works</h2>\n<p>${esc(data.why_it_works).replace(/\n/g, "<br>")}</p>\n\n<h2>Alternate Hook Options</h2>\n<ol>\n`;
    data.alternate_hooks?.forEach((h) => { html += `  <li>${esc(h)}</li>\n`; });
    html += `</ol>`;
    if (data.cta_variations?.length) {
      html += `\n\n<h2>CTA Variations</h2>\n<ol>\n`;
      data.cta_variations.forEach((c) => { html += `  <li>${esc(c)}</li>\n`; });
      html += `</ol>`;
    }
    return html;
  };

  const copyHtml = async () => {
    const html = getHtmlText();
    try {
      await navigator.clipboard.writeText(html);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = html;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedHtml(true);
    setTimeout(() => setCopiedHtml(false), 2000);
    toast.success("HTML copied!");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
      toast.success("Saved to history! +5 XP");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const resp = await api.post("/export/pdf", {
        title: exportMeta?.title || exportMeta?.topic || "Content Draft",
        content_type: exportMeta?.content_type || "",
        platform: exportMeta?.platform || "",
        audience: exportMeta?.audience || "",
        ai_model: exportMeta?.ai_model || "",
        draft: data.draft || "",
        core_angle: data.core_angle || "",
        why_it_works: data.why_it_works || "",
        alternate_hooks: data.alternate_hooks || [],
        cta_variations: Array.isArray(data.cta_variations) ? data.cta_variations.join("\n") : data.cta_variations || "",
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(exportMeta?.title || "content").substring(0, 50).replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
      data-testid="content-display"
    >
      <ContentActionBar
        onCopyDraft={() => copyToClipboard(data.draft, "draft")}
        onCopyAll={() => copyToClipboard(getAllText(), "all")}
        onSave={onSave ? handleSave : null}
        onEdit={onEdit}
        onRepurpose={onRepurpose}
        onExportPdf={handleExportPdf}
        onCopyMarkdown={copyMarkdown}
        onCopyHtml={copyHtml}
        onOpenGoogleDocs={openInGoogleDocs}
        copiedDraft={copiedDraft} copiedAll={copiedAll}
        copiedMarkdown={copiedMarkdown} copiedHtml={copiedHtml}
        isSaved={isSaved} saving={saving}
        exporting={exporting} gdocsExporting={gdocsExporting}
      />

      {resourcesUsed?.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]" data-testid="resources-used-tag">
          <Archive size={14} className="shrink-0" />
          <span>Generated with: {resourcesUsed.join(", ")}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6"
          data-testid="section-core-angle"
        >
          <h3 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-3">Core Angle</h3>
          <p className="text-[var(--text-secondary)] leading-relaxed">{data.core_angle}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6"
          data-testid="section-why-it-works"
        >
          <h3 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-3">Why It Works</h3>
          <div className="prose prose-sm max-w-none text-[var(--text-secondary)] leading-relaxed prose-headings:text-[var(--text-primary)] prose-strong:text-[var(--text-primary)] prose-p:text-[var(--text-secondary)]">
            <ReactMarkdown>{data.why_it_works}</ReactMarkdown>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 bg-[var(--bg-card)] border border-[#FF4400]/20 rounded-lg p-8 min-h-[400px]"
          data-testid="section-draft"
        >
          <h3 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-4">Draft</h3>
          <div className="prose max-w-none text-[var(--text-primary)] leading-relaxed text-base
            prose-headings:text-[var(--text-primary)] prose-headings:font-['Cormorant_Garamond'] prose-headings:font-semibold
            prose-h1:text-xl prose-h2:text-lg prose-h3:text-base
            prose-p:text-[var(--text-primary)] prose-p:leading-relaxed
            prose-strong:text-[var(--text-primary)] prose-strong:font-semibold
            prose-em:text-[var(--text-secondary)]
            prose-ul:text-sm prose-ol:text-sm prose-li:text-[var(--text-primary)]
            prose-a:text-[#FF4400] prose-a:no-underline hover:prose-a:underline
            prose-hr:border-[var(--border)]
            prose-blockquote:border-l-[#FF4400] prose-blockquote:text-[var(--text-muted)]
            prose-code:text-[#FF4400] prose-code:bg-[var(--bg-base)] prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs">
            <ReactMarkdown>{data.draft}</ReactMarkdown>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6"
          data-testid="section-alternate-hooks"
        >
          <h3 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-3">Alternate Hook Options</h3>
          <ol className="space-y-2">
            {data.alternate_hooks?.map((hook, i) => (
              <li key={`hook-${hook.slice(0, 25)}-${i}`} className="text-[var(--text-secondary)] leading-relaxed flex gap-2">
                <span className="text-[var(--success)] font-['JetBrains_Mono'] text-sm shrink-0">{i + 1}.</span>
                <span>{hook}</span>
              </li>
            ))}
          </ol>
        </motion.div>

        {data.cta_variations?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6"
            data-testid="section-cta-variations"
          >
            <h3 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400] mb-3">CTA Variations</h3>
            <ol className="space-y-2">
              {data.cta_variations.map((cta, i) => (
                <li key={`cta-${cta.slice(0, 25)}-${i}`} className="text-[var(--text-secondary)] leading-relaxed flex gap-2">
                  <span className="text-[var(--success)] font-['JetBrains_Mono'] text-sm shrink-0">{i + 1}.</span>
                  <span>{cta}</span>
                </li>
              ))}
            </ol>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};
